import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatHours } from '../monthly/format';
import { parseLocalDate } from '../../utils/dateHelpers';
import { cardSx, cardTitleSx, captionSx, DAY_COLORS, INK, MUTED, useThemeColors } from './homeTheme';

const STATUS_LABEL = {
  ok: 'Complete',
  partial: 'Partial absence',
  under: 'Fewer hours than required',
  missing: 'No hours',
  absence: 'Full-day absence',
  weekend: 'Weekend',
  today: 'Today',
  future: 'Forecast',
};

// Le giornate a 0 restano visibili con una piccola barra
const STUB_HOURS = 0.3;

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box sx={{ bgcolor: 'var(--rt-paper)', border: '1px solid var(--rt-line)', borderRadius: 2, px: 1.5, py: 1, boxShadow: '0 4px 16px var(--rt-hairline)' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: INK }}>{d.fullLabel}</Typography>
      <Typography sx={{ fontSize: 12.5, color: MUTED }}>
        {d.future ? `${formatHours(d.forecast)}h forecast` : `${formatHours(d.actual)}h tracked`}
        {d.target ? ` · ${formatHours(d.target)}h expected` : ''}
      </Typography>
      <Typography sx={{ fontSize: 12, color: DAY_COLORS[d.status] === '#C9CFC6' ? MUTED : DAY_COLORS[d.status], fontWeight: 600 }}>
        {STATUS_LABEL[d.status]}
      </Typography>
    </Box>
  );
}

/** Ore per giorno del mese, colorate per stato, con la linea delle 8 ore. */
export default function DailyHoursChart({ stats, onOpenDay }) {
  const c = useThemeColors();
  const data = (stats.days || []).map((d) => {
    const minutes = d.future ? d.forecast : d.actual;
    let hours = minutes / 60;
    if (!hours && (d.status === 'missing' || d.status === 'absence')) hours = STUB_HOURS;
    return {
      ...d,
      hours,
      fullLabel: parseLocalDate(d.dateKey).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }),
    };
  });

  // Asse delle ore a passi di 2h, almeno fino a 10h
  const yMax = Math.max(10, Math.ceil(Math.max(0, ...data.map((d) => d.hours)) / 2) * 2);

  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={cardTitleSx}>Daily hours</Typography>
        <Typography sx={captionSx}>Click a bar to open that day</Typography>
      </Box>
      {!stats.ready ? (
        <Skeleton variant="rounded" height={220} />
      ) : (
        <Box sx={{ height: 230 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 24, left: -12, bottom: 0 }} barCategoryGap="22%">
              <CartesianGrid vertical={false} stroke={c.track} />
              <XAxis
                dataKey="day"
                tickLine={false}
                axisLine={false}
                interval={0}
                tick={({ x, y, payload }) => {
                  const d = data[payload.index];
                  return (
                    <text x={x} y={y + 12} textAnchor="middle" fontSize={10.5} fontWeight={d?.today ? 800 : 500} fill={d?.weekend ? c.faint : d?.today ? c.ink : c.muted}>
                      {payload.value}
                    </text>
                  );
                }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                fontSize={11}
                tick={{ fill: c.muted }}
                domain={[0, yMax]}
                ticks={Array.from({ length: yMax / 2 + 1 }, (_, i) => i * 2)}
                tickFormatter={(v) => `${v}h`}
              />
              <ReferenceLine y={8} stroke={c['pill-border-hover']} strokeDasharray="4 4" label={{ value: '8h', position: 'right', fontSize: 11, fill: c.muted }} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(90,176,49,0.08)' }} />
              <Bar dataKey="hours" radius={[5, 5, 2, 2]} onClick={(d) => onOpenDay(d.dateKey)} style={{ cursor: 'pointer' }}>
                {data.map((d) => (
                  <Cell key={d.day} fill={DAY_COLORS[d.status]} fillOpacity={d.status === 'weekend' && !d.actual ? 0 : 1} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
        {['ok', 'under', 'missing', 'partial', 'absence', 'future'].map((k) => (
          <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.75, bgcolor: DAY_COLORS[k] }} />
            <Typography sx={{ fontSize: 12, color: MUTED }}>{STATUS_LABEL[k]}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
